/* custom-prints-widget — self-contained upload -> AI preview -> size/frame -> add-to-cart.
 * Framework-free. Renders into an element and reads config from window.CUSTOM_PRINTS_CONFIG:
 *   {
 *     apiBase: "https://custom-prints-api-shopify.onrender.com",  // required
 *     mount:   "#custom-prints-widget",                           // selector
 *     sizes:   [{ id, label, dims, price, compareAt, popular }],  // optional (defaults below)
 *     frame:   { price, colors: ["White","Espresso","Black"] },   // optional
 *     onAddToCart: async ({ variant, previewId, frame }) => {}    // theme wires Shopify cart here
 *   }
 */
(function () {
  "use strict";

  const cfg = window.CUSTOM_PRINTS_CONFIG || {};
  const API = (cfg.apiBase || "").replace(/\/$/, "");

  const SIZES = cfg.sizes || [
    { id: "s-10", label: "Canvas Print — Small", dims: '10 × 10"', price: 69, compareAt: 109 },
    { id: "s-12", label: "Canvas Print — Small", dims: '12 × 12"', price: 79, compareAt: 129 },
    { id: "m-16", label: "Canvas Print — Medium", dims: '16 × 16"', price: 115, compareAt: 179 },
    { id: "l-20", label: "Canvas Print — Large", dims: '20 × 20"', price: 139, compareAt: 239, popular: true },
    { id: "xl-24", label: "Canvas Print — XL", dims: '24 × 24"', price: 169, compareAt: 299 },
    { id: "r-32", label: "Canvas Print — Royal", dims: '32 × 32"', price: 279, compareAt: 449 }
  ];
  const FRAME = cfg.frame || { price: 46, colors: ["White", "Espresso", "Black"] };

  const LOADING_MESSAGES = [
    "Bringing your family together…",
    "Adding soft wings and gentle light…",
    "Blending everyone into one portrait…",
    "Warming the light so it feels real…",
    "Finishing the final touches…"
  ];

  // ── state ──────────────────────────────────────────────
  const state = {
    people: [], // { file, objectUrl, name, birthYear, deathYear, deceased }
    previews: [], // [{ id, previewUrl }] — the generated options
    selectedIndex: 0, // which option the customer picked
    galleryIndex: 0, // which example/gallery image is shown in the stage
    generating: false,
    error: "",
    selectedSizeId: (SIZES.find((s) => s.popular) || SIZES[0]).id,
    frameOn: false,
    frameColor: FRAME.colors[0]
  };

  const OPTIONS_COUNT = cfg.variations || 4;
  const selectedPreview = () => state.previews[state.selectedIndex] || null;

  const $ = (sel, root = document) => root.querySelector(sel);
  const money = (n) => "$" + Number(n).toFixed(2);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

  let root;

  function init() {
    root = $(cfg.mount || "#custom-prints-widget");
    if (!root) {
      console.error("[custom-prints] mount element not found:", cfg.mount);
      return;
    }
    if (!API) console.warn("[custom-prints] no apiBase configured");
    injectStyles();
    render();
  }

  // ── actions ────────────────────────────────────────────
  function addPhotos(fileList) {
    const files = Array.from(fileList).filter((f) => /^image\//.test(f.type));
    for (const file of files) {
      if (state.people.length >= 6) break;
      state.people.push({
        file,
        objectUrl: URL.createObjectURL(file),
        name: "",
        birthYear: "",
        deathYear: "",
        deceased: false
      });
    }
    state.error = "";
    render();
  }

  function removePerson(i) {
    URL.revokeObjectURL(state.people[i].objectUrl);
    state.people.splice(i, 1);
    render();
  }

  function selectedSize() {
    return SIZES.find((s) => s.id === state.selectedSizeId) || SIZES[0];
  }
  function totalPrice() {
    return selectedSize().price + (state.frameOn ? FRAME.price : 0);
  }

  async function generate() {
    if (!state.people.length || state.generating) return;
    state.generating = true;
    state.error = "";
    loadPct = 0;
    loadMsgIdx = 0;
    render(); // render() starts the loading animation while generating

    try {
      const form = new FormData();
      const people = state.people.map((p, i) => ({
        name: p.name,
        photoIndex: i,
        deceased: p.deceased,
        birthYear: p.birthYear,
        deathYear: p.deathYear
      }));
      state.people.forEach((p) => form.append("photos", p.file));
      form.append("people", JSON.stringify(people));

      form.append("variations", String(OPTIONS_COUNT));

      const res = await fetch(API + "/api/preview", { method: "POST", body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Something went wrong generating your preview.");

      // Prefer the options array; fall back to a single preview for older responses.
      state.previews = Array.isArray(data.previews) && data.previews.length
        ? data.previews
        : data.previewUrl ? [{ id: data.id, previewUrl: data.previewUrl }] : [];
      state.selectedIndex = 0;
    } catch (err) {
      state.error = err.message || "Preview failed. Please try again.";
    } finally {
      state.generating = false;
      stopLoadingAnimation();
      render();
    }
  }

  async function addToCart() {
    const chosen = selectedPreview();
    if (!chosen) return;
    const payload = {
      variant: selectedSize(),
      previewId: chosen.id,
      previewUrl: chosen.previewUrl,
      frame: state.frameOn ? { on: true, color: state.frameColor, price: FRAME.price } : { on: false },
      total: totalPrice()
    };
    if (typeof cfg.onAddToCart === "function") {
      try {
        await cfg.onAddToCart(payload);
      } catch (err) {
        toast("Could not add to cart: " + (err.message || err));
      }
    } else {
      // Standalone/demo mode — no Shopify cart wired yet.
      toast(`Added: ${payload.variant.label} ${payload.variant.dims}${payload.frame.on ? " + " + payload.frame.color + " frame" : ""} — ${money(payload.total)}`);
      console.log("[custom-prints] add-to-cart payload:", payload);
    }
  }

  // ── loading animation (no real progress available; stage it) ──
  // Progress persists across re-renders so interacting mid-generation doesn't reset it.
  let loadTimer = null;
  let loadPct = 0;
  let loadMsgIdx = 0;
  function startLoadingAnimation() {
    if (loadTimer) clearInterval(loadTimer); // idempotent
    loadTimer = setInterval(() => {
      // ease toward 95% over ~80s, then hold until the response lands
      loadPct = Math.min(95, loadPct + Math.max(0.4, (95 - loadPct) * 0.03));
      const bar = $(".cp-progress-fill", root);
      const label = $(".cp-loading-msg", root);
      if (bar) bar.style.width = loadPct.toFixed(1) + "%";
      if (label && loadPct > (loadMsgIdx + 1) * 18 && loadMsgIdx < LOADING_MESSAGES.length - 1) {
        loadMsgIdx += 1;
        label.textContent = LOADING_MESSAGES[loadMsgIdx];
      }
    }, 500);
  }
  function stopLoadingAnimation() {
    if (loadTimer) clearInterval(loadTimer);
    loadTimer = null;
  }

  function toast(text) {
    let t = $(".cp-toast", root);
    if (!t) {
      t = document.createElement("div");
      t.className = "cp-toast";
      root.appendChild(t);
    }
    t.textContent = text;
    t.classList.add("cp-toast--show");
    setTimeout(() => t.classList.remove("cp-toast--show"), 3500);
  }

  // ── render ─────────────────────────────────────────────
  function render() {
    root.innerHTML = `
      <div class="cp-grid">
        <div class="cp-left">${viewPreviewPane()}</div>
        <div class="cp-right">
          ${viewUploader()}
          ${viewResults()}
          ${viewSizes()}
          ${viewFrame()}
          ${viewCartBar()}
          ${viewTrust()}
        </div>
      </div>`;
    wireEvents();
    if (state.generating) startLoadingAnimation();
  }

  function viewPreviewPane() {
    const chosen = selectedPreview();
    if (chosen) {
      return `<div class="cp-canvas-wrap">
          <img class="cp-canvas" src="${esc(chosen.previewUrl)}" alt="Your personalized artwork preview" />
        </div>
        <p class="cp-canvas-note">Preview shown with watermark. Your print is high-definition and unmarked.</p>`;
    }
    const gallery = (cfg.galleryImages && cfg.galleryImages.length)
      ? cfg.galleryImages
      : (cfg.sampleImage ? [cfg.sampleImage] : []);
    if (gallery.length) {
      const idx = Math.min(state.galleryIndex || 0, gallery.length - 1);
      const thumbs = gallery.length > 1
        ? `<div class="cp-thumbs">${gallery
            .map((g, i) => `<button class="cp-thumb ${i === idx ? "cp-thumb--sel" : ""}" data-gthumb="${i}" aria-label="View image ${i + 1}"><img src="${esc(g)}" alt="" loading="lazy" /></button>`)
            .join("")}</div>`
        : "";
      return `<div class="cp-canvas-wrap">
          <img class="cp-canvas" src="${esc(gallery[idx])}" alt="Example artwork" />
          ${idx === 0 ? `<span class="cp-sample-tag">Example</span>` : ""}
        </div>
        ${thumbs}
        <p class="cp-canvas-note">Upload your photos and press <b>Create artwork</b> to see your own.</p>`;
    }
    return `<div class="cp-canvas-wrap cp-canvas-wrap--empty">
        <div class="cp-canvas-placeholder">
          <div class="cp-canvas-placeholder-inner">
            <div class="cp-ph-icon">🕊️</div>
            <div>Your artwork preview appears here</div>
            <div class="cp-ph-sub">Upload a photo and press <b>Create artwork</b></div>
          </div>
        </div>
      </div>`;
  }

  function viewUploader() {
    const chips = state.people
      .map(
        (p, i) => `
        <div class="cp-person" data-i="${i}">
          <div class="cp-person-top">
            <img class="cp-person-thumb" src="${esc(p.objectUrl)}" alt="" />
            <button class="cp-person-remove" data-remove="${i}" title="Remove">✕</button>
            <div class="cp-person-fields">
              <input class="cp-input" data-field="name" data-i="${i}" placeholder="Name (optional)" value="${esc(p.name)}" />
              <label class="cp-check">
                <input type="checkbox" data-field="deceased" data-i="${i}" ${p.deceased ? "checked" : ""}/>
                <span>In loving memory (add wings)</span>
              </label>
            </div>
          </div>
          ${p.deceased ? `<div class="cp-years">
              <input class="cp-input cp-input--year" data-field="birthYear" data-i="${i}" placeholder="Born (e.g. 1940)" value="${esc(p.birthYear)}" maxlength="4"/>
              <span class="cp-dash">–</span>
              <input class="cp-input cp-input--year" data-field="deathYear" data-i="${i}" placeholder="Passed (e.g. 2019)" value="${esc(p.deathYear)}" maxlength="4"/>
            </div>` : ""}
        </div>`
      )
      .join("");

    return `
      <div class="cp-card">
        <div class="cp-step">1 · Add your photos</div>
        <div class="cp-drop" id="cp-drop">
          <input type="file" id="cp-file" accept="image/*" multiple hidden />
          <div class="cp-drop-inner">
            <b>Click to upload</b> or drag photos here
            <div class="cp-drop-sub">One person per photo · face clearly visible · up to 6</div>
          </div>
        </div>
        <div class="cp-people">${chips}</div>
        ${state.error ? `<div class="cp-error">${esc(state.error)}</div>` : ""}
        <button class="cp-btn cp-btn--primary cp-generate" ${state.people.length && !state.generating ? "" : "disabled"}>
          ${state.generating ? "Creating…" : state.previews.length ? "↻ Try new versions" : "2 · Create artwork"}
        </button>
        <div class="cp-privacy">
          🔒 Your photos are private — used only to create your artwork, never shown publicly.
        </div>
      </div>`;
  }

  // Inline results: the loading state, then the grid of generated options.
  function viewResults() {
    if (state.generating) {
      const tiles = Array.from({ length: OPTIONS_COUNT }, () => `<div class="cp-opt cp-opt--loading"></div>`).join("");
      return `
        <div class="cp-card cp-results">
          <div class="cp-results-head">
            <span class="cp-spinner cp-spinner--sm"></span>
            <span class="cp-loading-msg">${LOADING_MESSAGES[0]}</span>
          </div>
          <div class="cp-progress"><div class="cp-progress-fill"></div></div>
          <div class="cp-results-sub">Creating ${OPTIONS_COUNT} designs — this takes up to a minute.</div>
          <div class="cp-options">${tiles}</div>
        </div>`;
    }
    if (!state.previews.length) return "";
    const tiles = state.previews
      .map(
        (p, i) => `
        <button class="cp-opt ${i === state.selectedIndex ? "cp-opt--sel" : ""}" data-pick="${i}">
          <img src="${esc(p.previewUrl)}" alt="Design option ${i + 1}" loading="lazy" />
          ${i === state.selectedIndex ? `<span class="cp-opt-check">✓</span>` : ""}
        </button>`
      )
      .join("");
    return `
      <div class="cp-card cp-results">
        <div class="cp-results-title">Pick your favorite design</div>
        <div class="cp-options">${tiles}</div>
        <div class="cp-oneofakind">✨ Each design is one-of-a-kind. Choose a size below and add to cart to save it.</div>
      </div>`;
  }

  function viewSizes() {
    const rows = SIZES.map((s) => {
      const off = s.compareAt ? Math.round((1 - s.price / s.compareAt) * 100) : 0;
      const sel = s.id === state.selectedSizeId;
      return `
        <button class="cp-size ${sel ? "cp-size--sel" : ""}" data-size="${s.id}">
          ${s.popular ? `<span class="cp-badge">Most Popular</span>` : ""}
          <span class="cp-size-main">
            <span class="cp-size-label">${esc(s.label)}</span>
            <span class="cp-size-dims">${esc(s.dims)}</span>
          </span>
          <span class="cp-size-price">
            <span class="cp-price-now">${money(s.price)}</span>
            ${s.compareAt ? `<span class="cp-price-was">${money(s.compareAt)}</span> <span class="cp-price-off">-${off}%</span>` : ""}
          </span>
        </button>`;
    }).join("");
    return `<div class="cp-card"><div class="cp-step cp-step--plain">Choose your size</div>${rows}</div>`;
  }

  function viewFrame() {
    const swatches = FRAME.colors
      .map(
        (c) => `<button class="cp-swatch ${state.frameColor === c ? "cp-swatch--sel" : ""}" data-frame-color="${esc(c)}">
            <span class="cp-swatch-dot cp-swatch-dot--${c.toLowerCase()}"></span>${esc(c)}
          </button>`
      )
      .join("");
    return `
      <div class="cp-card">
        <label class="cp-frame-toggle">
          <span><b>Add a floating frame</b> <span class="cp-frame-price">+${money(FRAME.price)}</span></span>
          <input type="checkbox" class="cp-frame-check" ${state.frameOn ? "checked" : ""}/>
        </label>
        ${state.frameOn ? `<div class="cp-swatches">${swatches}</div>` : ""}
      </div>`;
  }

  function viewCartBar() {
    const gated = !selectedPreview();
    return `
      <div class="cp-card cp-cartbar">
        <div class="cp-total">
          <span>Total</span>
          <span class="cp-total-amt">${money(totalPrice())}</span>
        </div>
        <button class="cp-btn cp-btn--primary cp-addcart" ${gated ? "disabled" : ""}>Add to Cart</button>
        ${gated ? `<div class="cp-gatemsg">Create your artwork and pick a design first</div>` : ""}
      </div>`;
  }

  function viewTrust() {
    return `<div class="cp-trust">🇺🇸 Printed &amp; shipped from the USA · Tracked &amp; insured · 30-Day Happiness Promise</div>`;
  }

  // ── events ─────────────────────────────────────────────
  function wireEvents() {
    const drop = $("#cp-drop", root);
    const file = $("#cp-file", root);
    if (drop && file) {
      drop.addEventListener("click", () => file.click());
      drop.addEventListener("dragover", (e) => { e.preventDefault(); drop.classList.add("cp-drop--over"); });
      drop.addEventListener("dragleave", () => drop.classList.remove("cp-drop--over"));
      drop.addEventListener("drop", (e) => {
        e.preventDefault();
        drop.classList.remove("cp-drop--over");
        if (e.dataTransfer.files) addPhotos(e.dataTransfer.files);
      });
      file.addEventListener("change", (e) => addPhotos(e.target.files));
    }

    root.querySelectorAll("[data-remove]").forEach((b) =>
      b.addEventListener("click", () => removePerson(Number(b.dataset.remove)))
    );

    root.querySelectorAll("[data-field]").forEach((el) => {
      const i = Number(el.dataset.i);
      const field = el.dataset.field;
      const evt = el.type === "checkbox" ? "change" : "input";
      el.addEventListener(evt, () => {
        if (field === "deceased") {
          state.people[i].deceased = el.checked;
          render(); // toggles the year fields
        } else {
          state.people[i][field] = el.value;
        }
      });
    });

    const gen = $(".cp-generate", root);
    if (gen) gen.addEventListener("click", generate);

    root.querySelectorAll("[data-size]").forEach((b) =>
      b.addEventListener("click", () => { state.selectedSizeId = b.dataset.size; render(); })
    );

    const frameCheck = $(".cp-frame-check", root);
    if (frameCheck) frameCheck.addEventListener("change", () => { state.frameOn = frameCheck.checked; render(); });

    root.querySelectorAll("[data-frame-color]").forEach((b) =>
      b.addEventListener("click", () => { state.frameColor = b.dataset.frameColor; render(); })
    );

    root.querySelectorAll("[data-pick]").forEach((b) =>
      b.addEventListener("click", () => { state.selectedIndex = Number(b.dataset.pick); render(); })
    );

    root.querySelectorAll("[data-gthumb]").forEach((b) =>
      b.addEventListener("click", () => { state.galleryIndex = Number(b.dataset.gthumb); render(); })
    );

    const cart = $(".cp-addcart", root);
    if (cart) cart.addEventListener("click", addToCart);
  }

  // ── styles ─────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById("cp-styles")) return;
    const css = `
    #custom-prints-widget, #custom-prints-widget * { box-sizing: border-box; }
    #custom-prints-widget { --cp-accent:#121212; --cp-order:#121212; --cp-pink:#121212; --cp-ink:#121212; --cp-muted:#6b6b6b; --cp-line:#e5e5e5; --cp-bg:#fff; --cp-soft:#f6f6f6;
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: var(--cp-ink); max-width: 1080px; margin: 0 auto; }
    .cp-grid { display:grid; grid-template-columns: 1.05fr 1fr; gap: 28px; align-items:start; }
    @media (max-width: 820px){ .cp-grid{ grid-template-columns:1fr; } }
    .cp-canvas-wrap { position:sticky; top:16px; border-radius:16px; overflow:hidden; background:var(--cp-soft); }
    .cp-canvas { width:100%; display:block; }
    .cp-sample-tag { position:absolute; top:10px; left:10px; background:#00000099; color:#fff; font-size:11px; font-weight:600; padding:3px 9px; border-radius:20px; }
    .cp-canvas-wrap--empty { aspect-ratio:1/1; display:flex; align-items:center; justify-content:center; border:1px dashed var(--cp-line); }
    .cp-canvas-placeholder { text-align:center; color:var(--cp-muted); }
    .cp-ph-icon { font-size:40px; margin-bottom:10px; }
    .cp-ph-sub { font-size:13px; margin-top:6px; opacity:.8; }
    .cp-canvas-note { font-size:12px; color:var(--cp-muted); text-align:center; margin:8px 2px 0; }
    .cp-thumbs { display:flex; gap:8px; margin-top:10px; flex-wrap:wrap; }
    .cp-thumb { padding:0; border:2px solid transparent; border-radius:9px; overflow:hidden; cursor:pointer; background:#eee; width:62px; height:62px; flex:none; transition:.15s; }
    .cp-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
    .cp-thumb--sel { border-color:var(--cp-accent); }
    .cp-thumb:hover { border-color:#bbb; }
    .cp-card { background:var(--cp-bg); border:1px solid var(--cp-line); border-radius:14px; padding:16px; margin-bottom:14px; }
    .cp-step { font-weight:700; font-size:15px; margin-bottom:12px; }
    .cp-step--plain { margin-bottom:10px; }
    .cp-drop { border:2px dashed #d9d9d9; border-radius:12px; padding:22px; text-align:center; cursor:pointer; transition:.15s; background:var(--cp-soft); }
    .cp-drop--over, .cp-drop:hover { border-color:var(--cp-accent); background:#f2f2f2; }
    .cp-drop-sub { font-size:12px; color:var(--cp-muted); margin-top:6px; }
    .cp-people { margin-top:12px; display:flex; flex-direction:column; gap:10px; }
    .cp-person { border:1px solid var(--cp-line); border-radius:10px; padding:10px; }
    .cp-person-top { display:flex; gap:10px; align-items:flex-start; position:relative; }
    .cp-person-thumb { width:54px; height:54px; border-radius:8px; object-fit:cover; flex:none; }
    .cp-person-remove { position:absolute; top:-4px; right:-4px; border:none; background:#00000088; color:#fff; width:20px; height:20px; border-radius:50%; cursor:pointer; font-size:11px; }
    .cp-person-fields { flex:1; display:flex; flex-direction:column; gap:6px; }
    .cp-input { width:100%; border:1px solid var(--cp-line); border-radius:8px; padding:8px 10px; font-size:14px; }
    .cp-input:focus { outline:none; border-color:var(--cp-accent); }
    .cp-check { display:flex; align-items:center; gap:7px; font-size:13px; color:#5a5a5a; cursor:pointer; }
    .cp-years { display:flex; align-items:center; gap:8px; margin-top:8px; padding-left:64px; }
    .cp-input--year { width:auto; flex:1; }
    .cp-dash { color:var(--cp-muted); }
    .cp-btn { width:100%; border:none; border-radius:10px; padding:14px; font-size:15px; font-weight:700; cursor:pointer; transition:.15s; }
    .cp-btn:disabled { opacity:.45; cursor:not-allowed; }
    .cp-btn--primary { background:linear-gradient(90deg,var(--cp-pink),#121212); color:#fff; margin-top:12px; }
    .cp-btn--primary:not(:disabled):hover { filter:brightness(1.05); }
    .cp-btn--order { background:var(--cp-order); color:#fff; }
    .cp-privacy { font-size:12px; color:var(--cp-muted); text-align:center; margin-top:10px; line-height:1.5; }
    .cp-error { background:#fff0f0; color:#c0392b; border:1px solid #f3c6c6; border-radius:8px; padding:10px; font-size:13px; margin-top:10px; }
    .cp-results { background:var(--cp-soft); border-color:#e5e5e5; }
    .cp-results-title { font-weight:700; margin-bottom:12px; text-align:center; }
    .cp-results-head { display:flex; align-items:center; gap:10px; justify-content:center; font-weight:700; margin-bottom:12px; }
    .cp-results-sub { font-size:12px; color:#6b6b6b; text-align:center; margin:10px 0 12px; }
    .cp-options { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
    .cp-opt { position:relative; padding:0; border:2px solid transparent; border-radius:10px; overflow:hidden; cursor:pointer; background:#eee; aspect-ratio:1/1; }
    .cp-opt img { width:100%; height:100%; object-fit:cover; display:block; }
    .cp-opt--sel { border-color:var(--cp-accent); box-shadow:0 0 0 2px #ededed; }
    .cp-opt-check { position:absolute; top:6px; right:6px; width:22px; height:22px; border-radius:50%; background:var(--cp-accent); color:#fff; font-size:13px; display:flex; align-items:center; justify-content:center; }
    .cp-opt--loading { background:linear-gradient(90deg,#eeeeee,#f5f5f5,#eeeeee); background-size:200% 100%; animation:cp-shimmer 1.3s linear infinite; cursor:default; }
    @keyframes cp-shimmer { to { background-position:-200% 0; } }
    .cp-oneofakind { font-size:12px; color:#6b6b6b; margin-top:12px; text-align:center; }
    .cp-size { position:relative; width:100%; display:flex; justify-content:space-between; align-items:center; gap:10px; background:#fff; border:1.5px solid var(--cp-line); border-radius:10px; padding:12px 14px; margin-bottom:8px; cursor:pointer; text-align:left; }
    .cp-size--sel { border-color:var(--cp-accent); box-shadow:0 0 0 2px #ededed; }
    .cp-size-label { font-weight:600; font-size:14px; display:block; }
    .cp-size-dims { font-size:12px; color:var(--cp-muted); }
    .cp-size-price { text-align:right; white-space:nowrap; }
    .cp-price-now { font-weight:700; }
    .cp-price-was { color:var(--cp-muted); text-decoration:line-through; font-size:12px; }
    .cp-price-off { color:#16a34a; font-size:12px; font-weight:700; }
    .cp-badge { position:absolute; top:-9px; left:12px; background:var(--cp-accent); color:#fff; font-size:10px; font-weight:700; padding:2px 8px; border-radius:20px; }
    .cp-frame-toggle { display:flex; justify-content:space-between; align-items:center; cursor:pointer; font-size:14px; }
    .cp-frame-price { color:var(--cp-accent); font-weight:700; }
    .cp-swatches { display:flex; gap:8px; margin-top:12px; }
    .cp-swatch { display:flex; align-items:center; gap:6px; border:1.5px solid var(--cp-line); background:#fff; border-radius:8px; padding:7px 10px; font-size:13px; cursor:pointer; }
    .cp-swatch--sel { border-color:var(--cp-accent); }
    .cp-swatch-dot { width:14px; height:14px; border-radius:50%; border:1px solid #0002; }
    .cp-swatch-dot--white{ background:#fff; } .cp-swatch-dot--espresso{ background:#4a2f22; } .cp-swatch-dot--black{ background:#1a1a1a; }
    .cp-cartbar { position:sticky; bottom:0; }
    .cp-total { display:flex; justify-content:space-between; align-items:center; font-weight:700; }
    .cp-total-amt { font-size:20px; }
    .cp-gatemsg { font-size:12px; color:var(--cp-muted); text-align:center; margin-top:8px; }
    .cp-trust { text-align:center; font-size:12px; color:var(--cp-muted); padding:4px; }
    .cp-spinner { width:42px; height:42px; border:4px solid #eee; border-top-color:var(--cp-accent); border-radius:50%; animation:cp-spin 1s linear infinite; }
    .cp-spinner--sm { width:20px; height:20px; border-width:3px; }
    @keyframes cp-spin { to { transform:rotate(360deg); } }
    .cp-loading-msg { font-size:15px; }
    .cp-progress { height:8px; background:#eaeaea; border-radius:20px; overflow:hidden; }
    .cp-progress-fill { height:100%; width:2%; background:linear-gradient(90deg,var(--cp-pink),var(--cp-accent)); transition:width .5s ease; }
    .cp-toast { position:fixed; left:50%; bottom:24px; transform:translateX(-50%) translateY(20px); background:#121212; color:#fff; padding:12px 18px; border-radius:10px; font-size:14px; opacity:0; pointer-events:none; transition:.25s; z-index:9999; max-width:90vw; }
    .cp-toast--show { opacity:1; transform:translateX(-50%) translateY(0); }
    `;
    const style = document.createElement("style");
    style.id = "cp-styles";
    style.textContent = css;
    document.head.appendChild(style);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
